<template>
    <section class="password">
        <h2 v-html="this.$i18n('account_profile_password_title')" />
        <div class="field" :class="{ focused: this.currentPasswordInputFocused }" v-if="this.passwordSet">
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9.5L19.0001 9.50006C20.1046 9.50006 21.0001 10.3955 21.0001 11.5001L21 19.5001C21 20.6046 20.1046 21.5001 19 21.5001H5C3.89543 21.5001 3 20.6046 3 19.5001L3.00002 11.5001C3.00002 10.3955 3.89545 9.50006 5.00002 9.50006H7V7.5C7 4.73858 9.23858 2.5 12 2.5C14.0503 2.5 15.8124 3.7341 16.584 5.5" stroke="#2C3143" stroke-width="2" stroke-linecap="round"/>
                <path d="M14 17.5V15.5C14 14.3954 13.1046 13.5 12 13.5V13.5C10.8954 13.5 10 14.3954 10 15.5V17.5" stroke="#2C3143" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <input type="password" name="current_password" ref="current_password"
                   @focus="currentPasswordInputFocused = true"
                   @blur="currentPasswordInputFocused = false"
                   v-model="currentPasswordLocal"
                   :placeholder="currentPasswordPlaceholder" />
            <reveal-icon v-if="newPasswordFieldVisible" @mousedown.native="revealCurrentPassword(true)" @mouseup.native="revealCurrentPassword(false)" @mouseleave.native="revealCurrentPassword(false)" />
            <edit-icon v-else @click.native="focusInput" />
        </div>
        <transition name="slide-down-fade">
            <div v-if="newPasswordFieldVisible" class="fieldWrapper">
                <div class="field password" :class="{ focused: this.newPasswordInputFocused }">
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9.5L19.0001 9.50006C20.1046 9.50006 21.0001 10.3955 21.0001 11.5001L21 19.5001C21 20.6046 20.1046 21.5001 19 21.5001H5C3.89543 21.5001 3 20.6046 3 19.5001L3.00002 11.5001C3.00002 10.3955 3.89545 9.50006 5.00002 9.50006H7V7.5C7 4.73858 9.23858 2.5 12 2.5C14.0503 2.5 15.8124 3.7341 16.584 5.5" stroke="#FAAA5A" stroke-width="2" stroke-linecap="round"/>
                        <path d="M14 17.5V15.5C14 14.3954 13.1046 13.5 12 13.5V13.5C10.8954 13.5 10 14.3954 10 15.5V17.5" stroke="#FAAA5A" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <input type="password" name="new_password" ref="new_password"
                           @focus="newPasswordInputFocused = true"
                           @blur="newPasswordInputFocused = false"
                           v-model="newPasswordLocal"
                           :placeholder="newPasswordPlaceholder" v-on:keyup.enter="save" />
                    <reveal-icon @mousedown.native="revealNewPassword(true)" @mouseup.native="revealNewPassword(false)" @mouseleave.native="revealNewPassword(false)" />
                </div>
                <div class="actions">
                    <v-button :i18n="{ id: 'account_profile_cancel_btn' }" @click.native="clear" :filled="false" size="medium" color="primary" />
                    <v-button :i18n="{ id: 'account_profile_input_action_save' }" @click.native="save" :filled="true" :disabled="!contentIsValid" size="medium" color="primary" />
                </div>
            </div>
        </transition>
    </section>
</template>

<script>
import i18nUtils from "Util/i18n.js";
import EditIcon from "./edit-icon.vue";
import VButton from "ViewComponent/v-button.vue";
import RevealIcon from "./reveal-icon.vue";

export default {
    name: 'section-password',
    components: { RevealIcon, VButton, EditIcon },
    props: {
        email: {
            type: String,
            default: ''
        },
        passwordSet: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            currentPasswordLocal: '',
            newPasswordLocal: '',
            currentPasswordInputFocused: false,
            newPasswordInputFocused: false
        }
    },
    computed: {
        currentPasswordPlaceholder() {
            return i18nUtils.prop('account_profile_input_placeholder_current_password')
        },
        newPasswordPlaceholder() {
            return i18nUtils.prop('account_profile_input_placeholder_new_password')
        },
        newPasswordFieldVisible() {
            return this.currentPasswordLocal.length > 0 || !this.passwordSet;
        },
        contentIsValid() {
            return this.newPasswordLocal.length >= 4 && (this.currentPasswordLocal.length > 0 || !this.passwordSet);
        }
    },
    methods: {
        clear() {
            this.currentPasswordInputFocused = false;
            this.currentPasswordLocal = '';
            this.newPasswordLocal = '';
            if (this.passwordSet) {
                this.$refs.current_password.blur();
            }
            this.$refs.new_password.blur();
        },
        save() {
            if (this.passwordSet) {
                this.$emit('save', { current_password: this.currentPasswordLocal, new_password: this.newPasswordLocal });
            } else {
                this.$emit('save', { new_password: this.newPasswordLocal });
            }

            this.clear();
        },
        focusInput() {
            if (this.passwordSet) {
                this.currentPasswordInputFocused = true;
                this.$refs.current_password.focus();
            }
        },
        revealCurrentPassword(state) {
            this.$refs.current_password.type = (state) ? 'text' : 'password';
        },
        revealNewPassword(state) {
            this.$refs.new_password.type = (state) ? 'text' : 'password';
        }
    },
}
</script>

<style lang="scss" scoped>
    @import "~Styles/colors";
    @import "~Styles/mixins";

    section.password {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 0 .5rem;
        > h2 {
            font-size: fontSize(16);
            line-height: fontSize(20);
        }
        > div.fieldWrapper {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        div.field {
            display: flex;
            flex-direction: row;
            gap: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid $colorBackgroundDarker;
            &.focused {
                border-bottom: 1px solid $colorBackgroundDark;
            }
            > input {
                flex: 1;
            }
            > svg.edit-icon {
                margin-left: auto;
                cursor: pointer;
                transition: all 150ms;
                &:hover {
                    transform: scale(1.2);
                }
            }
            &.password {
                &.focused {
                    border-bottom: 1px solid $colorAttention;
                }
            }
        }
        div.actions {
            display: flex;
            flex-direction: row;
            gap: 1rem;
            justify-content: flex-end;
            align-items: center;
        }
        .slide-down-fade-enter-active {
            animation: fade-slide-in 0.3s;
        }
        .slide-down-fade-leave-active {
            animation: fade-slide-in 0.3s reverse;
        }
        @keyframes fade-slide-in {
            0% {
                opacity: 0;
                transform: translateY(-100px);
            }
            25% {
                opacity: 0;
            }
            50% {
                opacity: 0;
            }
            75% {
                opacity: 0.4;
            }
            100% {
                opacity: 1;
                transform: translateY(0);
            }
        }
    }
</style>
